import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { Plus, Search, DollarSign, Upload, Download, FileSpreadsheet, CheckCircle2, XCircle, AlertTriangle, Pencil, Trash2 } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';

export default function ServiceCatalog() {
  const { hasPermission } = useAuth();
  const [services, setServices] = useState([]);
  const [states, setStates] = useState([]);
  const [branches, setBranches] = useState([]);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ name: '', code: '', description: '', unit: 'hour', rate_per_unit: '', category: 'Core Support', claim_types: ['NDIA', 'PLAN_MANAGED', 'SELF_MANAGED'], state_id: '', branch_id: '' });
  const [loading, setLoading] = useState(false);
  const [pricingDialog, setPricingDialog] = useState(null);
  const [pricingVersions, setPricingVersions] = useState([]);
  const [versionForm, setVersionForm] = useState({ effective_from: '', rate: '', notes: '' });
  const [addVersionDialog, setAddVersionDialog] = useState(false);

  // Bulk Upload state
  const [uploadDialog, setUploadDialog] = useState(false);
  const [uploadStep, setUploadStep] = useState(1); // 1=upload, 2=preview, 3=result
  const [uploadFile, setUploadFile] = useState(null);
  const [importMode, setImportMode] = useState('CREATE_ONLY');
  const [uploadJob, setUploadJob] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [importing, setImporting] = useState(false);

  // Edit / Delete state
  const [editDialog, setEditDialog] = useState(null); // service obj being edited
  const [editForm, setEditForm] = useState({ name: '', code: '', description: '', unit: 'hour', rate_per_unit: '', category: 'Core Support', claim_types: [], is_active: true });
  const [editSaving, setEditSaving] = useState(false);

  const [deleteDialog, setDeleteDialog] = useState(null); // service obj being deleted
  const [deleteUsage, setDeleteUsage] = useState(null);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteReason, setDeleteReason] = useState('');
  const [deleting, setDeleting] = useState(false);

  const canEdit = hasPermission('service_catalog.edit') || hasPermission('services.manage');
  const canDelete = hasPermission('service_catalog.delete');

  const fetchData = async () => {
    try {
      const [res, statesRes, branchesRes] = await Promise.all([
        api.get('/services/catalog'),
        api.get('/org/states'),
        api.get('/org/branches'),
      ]);
      setServices(res.data.data || []);
      setStates(statesRes.data.data || []);
      setBranches(branchesRes.data.data || []);
    } catch (e) {
      toast.error('Failed to load services');
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/services/catalog', { ...form, rate_per_unit: parseFloat(form.rate_per_unit) });
      toast.success('Service created');
      setDialogOpen(false);
      setForm({ name: '', code: '', description: '', unit: 'hour', rate_per_unit: '', category: 'Core Support', state_id: '', branch_id: '' });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create service');
    } finally {
      setLoading(false);
    }
  };

  const filtered = services.filter((s) =>
    s.name?.toLowerCase().includes(search.toLowerCase()) || s.code?.toLowerCase().includes(search.toLowerCase())
  );

  const handleDownloadTemplate = async () => {
    try {
      const res = await api.get('/service-catalog/import/template', { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'service_catalog_template.csv'; a.click();
      toast.success('Template downloaded');
    } catch { toast.error('Download failed'); }
  };

  const handleUploadValidate = async () => {
    if (!uploadFile) { toast.error('Select a file'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', uploadFile);
      fd.append('importMode', importMode);
      fd.append('errorMode', 'PARTIAL');
      const { data } = await api.post('/service-catalog/import/validate', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setUploadJob(data.data);
      setUploadStep(2);
      toast.success(`Validated: ${data.data.summary.validRows} valid, ${data.data.summary.invalidRows} invalid`);
    } catch (e) { toast.error(e.response?.data?.error?.message || e.response?.data?.detail || 'Upload failed'); }
    setUploading(false);
  };

  const handleExecuteImport = async () => {
    if (!uploadJob?.jobId) return;
    setImporting(true);
    try {
      const { data } = await api.post(`/service-catalog/import/${uploadJob.jobId}/execute`);
      setUploadJob(prev => ({ ...prev, result: data.data }));
      setUploadStep(3);
      toast.success(`Imported: ${data.data.summary.created} created, ${data.data.summary.updated} updated`);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.error?.message || 'Import failed'); }
    setImporting(false);
  };

  const handleDownloadErrors = async () => {
    if (!uploadJob?.jobId) return;
    try {
      const res = await api.get(`/service-catalog/import/${uploadJob.jobId}/errors`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = `import_errors.csv`; a.click();
    } catch { toast.error('Download failed'); }
  };

  const resetUpload = () => { setUploadDialog(false); setUploadStep(1); setUploadFile(null); setUploadJob(null); setImportMode('CREATE_ONLY'); };

  // ── Edit ──
  const openEdit = (svc) => {
    setEditForm({
      name: svc.name || '',
      code: svc.code || '',
      description: svc.description || '',
      unit: svc.unit || 'hour',
      rate_per_unit: svc.rate_per_unit ?? '',
      category: svc.category || 'Core Support',
      claim_types: svc.claim_types || ['NDIA', 'PLAN_MANAGED', 'SELF_MANAGED'],
      is_active: svc.is_active !== false,
    });
    setEditDialog(svc);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editDialog) return;
    setEditSaving(true);
    try {
      const payload = { ...editForm, rate_per_unit: parseFloat(editForm.rate_per_unit) };
      await api.put(`/services/catalog/${editDialog.id}`, payload);
      toast.success('Service item updated');
      setEditDialog(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail?.message || err.response?.data?.detail || 'Failed to update');
    } finally {
      setEditSaving(false);
    }
  };

  // ── Delete (safe / soft archive) ──
  const openDelete = async (svc) => {
    setDeleteDialog(svc);
    setDeletePassword('');
    setDeleteReason('');
    setDeleteUsage(null);
    try {
      const { data } = await api.get(`/services/catalog/${svc.id}/usage`);
      setDeleteUsage(data.data);
    } catch {
      setDeleteUsage({ usage: {}, can_delete: false });
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteDialog) return;
    if (!deletePassword) { toast.error('Password is required'); return; }
    setDeleting(true);
    try {
      await api.delete(`/services/catalog/${deleteDialog.id}`, {
        data: { password: deletePassword, reason: deleteReason || undefined },
      });
      toast.success('Service item archived');
      setDeleteDialog(null);
      fetchData();
    } catch (err) {
      const d = err.response?.data?.detail;
      const code = d?.code;
      if (code === 'SERVICE_ITEM_IN_USE') {
        toast.error(d.message || 'Cannot delete: service item is in use');
      } else if (code === 'INVALID_PASSWORD') {
        toast.error('Password verification failed');
      } else {
        toast.error(d?.message || d || 'Failed to delete');
      }
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Service Catalog</h1>
          <p className="text-muted-foreground mt-1">NDIS service items and rates</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleDownloadTemplate} data-testid="download-template-btn"><Download className="h-4 w-4 mr-2" />Template</Button>
          <Button variant="outline" onClick={() => setUploadDialog(true)} data-testid="upload-sheet-btn"><Upload className="h-4 w-4 mr-2" />Upload Sheet</Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="service-create-button"><Plus className="h-4 w-4 mr-2" /> Add Service</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Create Service</DialogTitle><DialogDescription>Add a new service item to the catalog</DialogDescription></DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required data-testid="service-name-input" /></div>
                <div className="space-y-2"><Label>Code</Label><Input value={form.code} onChange={(e) => setForm({...form, code: e.target.value})} required className="font-mono" data-testid="service-code-input" placeholder="NDIS-01-001" /></div>
              </div>
              <div className="space-y-2"><Label>Description</Label><Input value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} /></div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2"><Label>Unit</Label><Input value={form.unit} onChange={(e) => setForm({...form, unit: e.target.value})} /></div>
                <div className="space-y-2"><Label>Rate ($/unit)</Label><Input type="number" step="0.01" value={form.rate_per_unit} onChange={(e) => setForm({...form, rate_per_unit: e.target.value})} required data-testid="service-rate-input" /></div>
                <div className="space-y-2"><Label>Category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({...form, category: v})}>
                    <SelectTrigger data-testid="service-category-select"><SelectValue placeholder="Select category" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Core Support">Core Support</SelectItem>
                      <SelectItem value="Capacity Building">Capacity Building</SelectItem>
                      <SelectItem value="Capital">Capital</SelectItem>
                      <SelectItem value="Transport">Transport</SelectItem>
                      <SelectItem value="Consumables">Consumables</SelectItem>
                      <SelectItem value="Daily Activities">Daily Activities</SelectItem>
                      <SelectItem value="Community Access">Community Access</SelectItem>
                      <SelectItem value="Employment Support">Employment Support</SelectItem>
                      <SelectItem value="Therapy">Therapy</SelectItem>
                      <SelectItem value="Behaviour Support">Behaviour Support</SelectItem>
                      <SelectItem value="SIL">SIL</SelectItem>
                      <SelectItem value="SDA">SDA</SelectItem>
                      <SelectItem value="Respite">Respite</SelectItem>
                      <SelectItem value="Plan Management">Plan Management</SelectItem>
                      <SelectItem value="Coordination of Supports">Coordination of Supports</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Claim Types *</Label>
                <div className="flex flex-wrap gap-3">
                  {[{v:'NDIA',l:'NDIA Managed'},{v:'PLAN_MANAGED',l:'Plan Managed'},{v:'SELF_MANAGED',l:'Self Managed'}].map(ct => (
                    <label key={ct.v} className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={(form.claim_types || []).includes(ct.v)} onChange={e => {
                        const types = form.claim_types || [];
                        setForm({...form, claim_types: e.target.checked ? [...types, ct.v] : types.filter(t => t !== ct.v)});
                      }} className="rounded h-4 w-4 accent-primary" data-testid={`claim-type-${ct.v}`} />
                      <span className="text-sm">{ct.l}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>State</Label>
                  <Select value={form.state_id || '__none__'} onValueChange={(v) => setForm({...form, state_id: v === '__none__' ? '' : v})}>
                    <SelectTrigger><SelectValue placeholder="Org-wide" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Org-wide (all states)</SelectItem>
                      {states.map((s) => <SelectItem key={s.code} value={s.code}>{s.code} - {s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Branch</Label>
                  <Select value={form.branch_id || '__none__'} onValueChange={(v) => setForm({...form, branch_id: v === '__none__' ? '' : v})}>
                    <SelectTrigger><SelectValue placeholder="Org-wide" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Org-wide (all branches)</SelectItem>
                      {branches.map((b) => <SelectItem key={b.code} value={b.code}>{b.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button type="submit" className="w-full" disabled={loading} data-testid="service-submit-button">{loading ? 'Creating...' : 'Create Service'}</Button>
            </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search services..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Code</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Claim Types</TableHead>
                <TableHead>Unit</TableHead>
                <TableHead className="text-right">Rate</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Pricing</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow><TableCell colSpan={9} className="text-center py-8 text-muted-foreground">No services found</TableCell></TableRow>
              ) : filtered.map((s) => (
                <TableRow key={s.id} className="hover:bg-muted/60">
                  <TableCell className="font-medium">{s.name}</TableCell>
                  <TableCell className="font-mono text-xs">{s.code}</TableCell>
                  <TableCell>{s.category}</TableCell>
                  <TableCell><div className="flex flex-wrap gap-1">{(s.claim_types || []).map(ct => <Badge key={ct} variant="outline" className="text-[10px]">{ct === 'NDIA' ? 'NDIA' : ct === 'PLAN_MANAGED' ? 'Plan' : 'Self'}</Badge>)}</div></TableCell>
                  <TableCell>{s.unit}</TableCell>
                  <TableCell className="text-right font-mono tabular-nums">${(s.rate_per_unit || 0).toFixed(2)}</TableCell>
                  <TableCell><StatusBadge status={s.is_active ? 'active' : 'inactive'} /></TableCell>
                  <TableCell>
                    {hasPermission('services.manage_pricing') && (
                      <Button variant="ghost" size="sm" onClick={async () => {
                        try {
                          const res = await api.get(`/services/catalog/${s.id}/pricing`);
                          setPricingVersions(res.data.data?.versions || []);
                          setPricingDialog(s);
                        } catch(e) { toast.error('Failed to load pricing'); }
                      }} data-testid={`pricing-${s.id}`}><DollarSign className="h-3 w-3" /></Button>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      {canEdit && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEdit(s)}
                          data-testid={`service-edit-${s.id}`}
                          title="Edit service item"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      {canDelete && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openDelete(s)}
                          data-testid={`service-delete-${s.id}`}
                          title="Delete (archive) service item"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pricing Versions Dialog */}
      <Dialog open={!!pricingDialog} onOpenChange={() => setPricingDialog(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Pricing: {pricingDialog?.name}</DialogTitle>
            <DialogDescription>Rate versions for {pricingDialog?.code}</DialogDescription>
          </DialogHeader>
          <Table>
            <TableHeader><TableRow><TableHead>From</TableHead><TableHead>To</TableHead><TableHead className="text-right">Rate</TableHead><TableHead>Notes</TableHead></TableRow></TableHeader>
            <TableBody>
              {pricingVersions.length === 0 ? <TableRow><TableCell colSpan={4} className="text-center py-4 text-muted-foreground">No versions</TableCell></TableRow>
              : pricingVersions.map((v,i) => (
                <TableRow key={v.version_id || i}>
                  <TableCell className="font-mono text-xs">{v.effective_from}</TableCell>
                  <TableCell className="font-mono text-xs">{v.effective_to || '(open)'}</TableCell>
                  <TableCell className="text-right font-mono tabular-nums font-medium">${v.rate?.toFixed(2)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{v.notes || '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Button size="sm" onClick={() => setAddVersionDialog(true)} data-testid="add-version-button"><Plus className="h-3 w-3 mr-1" /> Add Version</Button>
        </DialogContent>
      </Dialog>

      {/* Add Version Dialog */}
      <Dialog open={addVersionDialog} onOpenChange={setAddVersionDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Pricing Version</DialogTitle><DialogDescription>New rate effective from date</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Effective From</Label><Input type="date" value={versionForm.effective_from} onChange={(e) => setVersionForm({...versionForm, effective_from: e.target.value})} required data-testid="version-date" /></div>
              <div className="space-y-2"><Label>Rate ($)</Label><Input type="number" step="0.01" value={versionForm.rate} onChange={(e) => setVersionForm({...versionForm, rate: e.target.value})} required data-testid="version-rate" /></div>
            </div>
            <div className="space-y-2"><Label>Notes</Label><Input value={versionForm.notes} onChange={(e) => setVersionForm({...versionForm, notes: e.target.value})} /></div>
            <Button className="w-full" disabled={!versionForm.effective_from || !versionForm.rate} onClick={async () => {
              try {
                await api.post(`/services/catalog/${pricingDialog?.id}/pricing-version`, {
                  effective_from: versionForm.effective_from, rate: parseFloat(versionForm.rate), notes: versionForm.notes,
                });
                toast.success('Pricing version added');
                setAddVersionDialog(false); setVersionForm({effective_from:'',rate:'',notes:''});
                const res = await api.get(`/services/catalog/${pricingDialog?.id}/pricing`);
                setPricingVersions(res.data.data?.versions || []);
                fetchData();
              } catch(e) { toast.error(e.response?.data?.error?.message || 'Failed'); }
            }} data-testid="version-submit">Add Version</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Upload Dialog */}
      <Dialog open={uploadDialog} onOpenChange={v => { if (!v) resetUpload(); }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileSpreadsheet className="w-5 h-5" />Bulk Upload Services</DialogTitle>
            <DialogDescription>Upload CSV or Excel file to import services</DialogDescription>
          </DialogHeader>

          {uploadStep === 1 && (
            <div className="space-y-4">
              <div>
                <Label>File (CSV, XLSX, XLS) *</Label>
                <Input type="file" accept=".csv,.xlsx,.xls" onChange={e => setUploadFile(e.target.files?.[0] || null)} data-testid="upload-file-input" />
              </div>
              <div>
                <Label>Import Mode</Label>
                <Select value={importMode} onValueChange={setImportMode}>
                  <SelectTrigger data-testid="import-mode-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CREATE_ONLY">Create New Only</SelectItem>
                    <SelectItem value="UPSERT">Create + Update Existing</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button className="w-full" onClick={handleUploadValidate} disabled={uploading || !uploadFile} data-testid="validate-upload-btn">
                <Upload className="w-4 h-4 mr-2" />{uploading ? 'Validating...' : 'Upload & Validate'}
              </Button>
            </div>
          )}

          {uploadStep === 2 && uploadJob && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <Card><CardContent className="py-3 text-center"><p className="text-2xl font-bold">{uploadJob.summary?.totalRows || 0}</p><p className="text-xs text-muted-foreground">Total Rows</p></CardContent></Card>
                <Card className="border-green-200"><CardContent className="py-3 text-center"><p className="text-2xl font-bold text-green-600">{uploadJob.summary?.validRows || 0}</p><p className="text-xs text-muted-foreground">Valid</p></CardContent></Card>
                <Card className={uploadJob.summary?.invalidRows > 0 ? 'border-red-200' : ''}><CardContent className="py-3 text-center"><p className={`text-2xl font-bold ${uploadJob.summary?.invalidRows > 0 ? 'text-red-600' : ''}`}>{uploadJob.summary?.invalidRows || 0}</p><p className="text-xs text-muted-foreground">Invalid</p></CardContent></Card>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Card><CardContent className="py-2 text-center"><p className="text-lg font-bold text-blue-600">{uploadJob.summary?.createCount || 0}</p><p className="text-xs text-muted-foreground">To Create</p></CardContent></Card>
                <Card><CardContent className="py-2 text-center"><p className="text-lg font-bold text-amber-600">{uploadJob.summary?.updateCount || 0}</p><p className="text-xs text-muted-foreground">To Update</p></CardContent></Card>
              </div>

              {/* Row preview */}
              <div className="max-h-60 overflow-y-auto border rounded-md">
                <table className="w-full text-sm">
                  <thead className="bg-muted sticky top-0"><tr><th className="px-2 py-1 text-left">Row</th><th className="px-2 py-1 text-left">Code</th><th className="px-2 py-1 text-left">Name</th><th className="px-2 py-1 text-left">Action</th><th className="px-2 py-1 text-left">Status</th></tr></thead>
                  <tbody>
                    {(uploadJob.rows || []).map((r, i) => (
                      <tr key={i} className={`border-b ${r.validationStatus === 'ERROR' ? 'bg-red-50' : ''}`}>
                        <td className="px-2 py-1 font-mono text-xs">{r.rowNumber}</td>
                        <td className="px-2 py-1 font-mono text-xs">{r.serviceCode}</td>
                        <td className="px-2 py-1">{r.serviceName}</td>
                        <td className="px-2 py-1"><Badge variant={r.actionType === 'CREATE' ? 'default' : r.actionType === 'UPDATE' ? 'secondary' : 'destructive'} className="text-[10px]">{r.actionType}</Badge></td>
                        <td className="px-2 py-1">
                          {r.validationStatus === 'VALID' ? <CheckCircle2 className="w-4 h-4 text-green-500" /> : <span className="text-xs text-red-600">{(r.errorDetailsJson || [])[0]?.message || 'Error'}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex gap-2">
                {uploadJob.summary?.invalidRows > 0 && <Button variant="outline" size="sm" onClick={handleDownloadErrors}><Download className="w-3 h-3 mr-1" />Error Report</Button>}
                <div className="flex-1" />
                <Button variant="outline" onClick={resetUpload}>Cancel</Button>
                <Button onClick={handleExecuteImport} disabled={importing || (uploadJob.summary?.validRows || 0) === 0} data-testid="execute-import-btn">
                  {importing ? 'Importing...' : `Import ${uploadJob.summary?.validRows || 0} Services`}
                </Button>
              </div>
            </div>
          )}

          {uploadStep === 3 && uploadJob?.result && (
            <div className="space-y-4 text-center">
              <CheckCircle2 className="w-12 h-12 mx-auto text-green-500" />
              <h3 className="text-lg font-bold">Import Complete</h3>
              <div className="grid grid-cols-3 gap-3">
                <Card className="border-green-200"><CardContent className="py-3"><p className="text-2xl font-bold text-green-600">{uploadJob.result.summary?.created || 0}</p><p className="text-xs text-muted-foreground">Created</p></CardContent></Card>
                <Card><CardContent className="py-3"><p className="text-2xl font-bold text-amber-600">{uploadJob.result.summary?.updated || 0}</p><p className="text-xs text-muted-foreground">Updated</p></CardContent></Card>
                <Card className={uploadJob.result.summary?.failed > 0 ? 'border-red-200' : ''}><CardContent className="py-3"><p className="text-2xl font-bold">{uploadJob.result.summary?.failed || 0}</p><p className="text-xs text-muted-foreground">Failed</p></CardContent></Card>
              </div>
              <Button onClick={resetUpload} className="w-full" data-testid="close-import-btn">Done</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Service Dialog */}
      <Dialog open={!!editDialog} onOpenChange={(v) => { if (!v) setEditDialog(null); }}>
        <DialogContent className="max-w-lg" data-testid="service-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit Service Item</DialogTitle>
            <DialogDescription>Update {editDialog?.name} ({editDialog?.code})</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Name</Label><Input value={editForm.name} onChange={(e) => setEditForm({...editForm, name: e.target.value})} required data-testid="service-edit-name" /></div>
              <div className="space-y-2"><Label>Code</Label><Input value={editForm.code} onChange={(e) => setEditForm({...editForm, code: e.target.value})} required className="font-mono" data-testid="service-edit-code" /></div>
            </div>
            <div className="space-y-2"><Label>Description</Label><Input value={editForm.description} onChange={(e) => setEditForm({...editForm, description: e.target.value})} /></div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2"><Label>Unit</Label><Input value={editForm.unit} onChange={(e) => setEditForm({...editForm, unit: e.target.value})} /></div>
              <div className="space-y-2"><Label>Rate ($/unit)</Label><Input type="number" step="0.01" value={editForm.rate_per_unit} onChange={(e) => setEditForm({...editForm, rate_per_unit: e.target.value})} required data-testid="service-edit-rate" /></div>
              <div className="space-y-2"><Label>Category</Label>
                <Select value={editForm.category} onValueChange={(v) => setEditForm({...editForm, category: v})}>
                  <SelectTrigger data-testid="service-edit-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['Core Support','Capacity Building','Capital','Transport','Consumables','Daily Activities','Community Access','Employment Support','Therapy','Behaviour Support','SIL','SDA','Respite','Plan Management','Coordination of Supports','Other'].map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Claim Types</Label>
              <div className="flex flex-wrap gap-3">
                {[{v:'NDIA',l:'NDIA Managed'},{v:'PLAN_MANAGED',l:'Plan Managed'},{v:'SELF_MANAGED',l:'Self Managed'}].map(ct => (
                  <label key={ct.v} className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={(editForm.claim_types || []).includes(ct.v)} onChange={e => {
                      const types = editForm.claim_types || [];
                      setEditForm({...editForm, claim_types: e.target.checked ? [...types, ct.v] : types.filter(t => t !== ct.v)});
                    }} className="rounded h-4 w-4 accent-primary" />
                    <span className="text-sm">{ct.l}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="edit-active" checked={editForm.is_active} onChange={(e) => setEditForm({...editForm, is_active: e.target.checked})} className="rounded h-4 w-4 accent-primary" />
              <Label htmlFor="edit-active" className="text-sm">Active (visible in dropdowns)</Label>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setEditDialog(null)}>Cancel</Button>
              <Button type="submit" disabled={editSaving} data-testid="service-edit-submit">{editSaving ? 'Saving...' : 'Save Changes'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete (Archive) Service Dialog */}
      <Dialog open={!!deleteDialog} onOpenChange={(v) => { if (!v) { setDeleteDialog(null); setDeletePassword(''); setDeleteReason(''); setDeleteUsage(null); } }}>
        <DialogContent className="max-w-md" data-testid="service-delete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700"><AlertTriangle className="w-5 h-5" /> Delete Service Item</DialogTitle>
            <DialogDescription>
              You are about to archive <span className="font-semibold">{deleteDialog?.name}</span> ({deleteDialog?.code}).
            </DialogDescription>
          </DialogHeader>

          <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            <p className="font-medium">Deleting a service item is permanent. This action is only allowed if the item has never been used.</p>
          </div>

          {deleteUsage && (
            <div className="rounded-md border bg-muted/40 p-3 text-xs space-y-1">
              <p className="font-medium text-muted-foreground mb-1">Current usage check:</p>
              <div className="grid grid-cols-2 gap-1">
                <div>Participant services: <span className="font-mono">{deleteUsage.usage?.participant_service_count ?? 0}</span></div>
                <div>Shifts: <span className="font-mono">{deleteUsage.usage?.shift_count ?? 0}</span></div>
                <div>Timesheets: <span className="font-mono">{deleteUsage.usage?.timesheet_count ?? 0}</span></div>
                <div>Invoices: <span className="font-mono">{deleteUsage.usage?.invoice_count ?? 0}</span></div>
                <div>Claims: <span className="font-mono">{deleteUsage.usage?.claim_count ?? 0}</span></div>
                <div>Agreements: <span className="font-mono">{deleteUsage.usage?.agreement_count ?? 0}</span></div>
              </div>
              {!deleteUsage.can_delete && (
                <p className="text-red-700 mt-2 font-medium">This item is in use and cannot be deleted.</p>
              )}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="delete-password">Confirm your password *</Label>
            <Input
              id="delete-password"
              type="password"
              value={deletePassword}
              onChange={(e) => setDeletePassword(e.target.value)}
              placeholder="Enter your account password"
              autoComplete="current-password"
              data-testid="service-delete-password"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="delete-reason">Reason (optional)</Label>
            <Input
              id="delete-reason"
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              placeholder="Why are you archiving this item?"
              data-testid="service-delete-reason"
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => setDeleteDialog(null)}>Cancel</Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={deleting || !deletePassword || (deleteUsage && !deleteUsage.can_delete)}
              data-testid="service-delete-confirm"
            >
              {deleting ? 'Archiving...' : 'Archive Service Item'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
